import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import { ArrowLeft, Sparkles, Loader2 } from "lucide-react";

function slugify(s) {
  return s.toLowerCase().replace(/[äöüß]/g, (c) => ({ ä: "ae", ö: "oe", ü: "ue", ß: "ss" }[c])).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

const EMPTY = {
  slug: "", title: "", excerpt: "", content: "", cover_image: "", cover_alt: "",
  meta_title: "", meta_description: "", category: "Grundlagen", reading_time: 5, status: "draft",
};

export default function AdminArticleEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [genPrompt, setGenPrompt] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    document.title = isNew ? "Neuer Artikel – Redaktion" : "Artikel bearbeiten – Redaktion";
    if (!isNew) {
      api.get(`/admin/articles/${id}`).then((r) => setForm(r.data)).catch(() => toast.error("Artikel nicht gefunden")).finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (status) => {
    setSaving(true);
    const payload = { ...form, status };
    if (!payload.slug) payload.slug = slugify(payload.title);
    try {
      if (isNew) {
        await api.post("/admin/articles", payload);
        toast.success("Artikel angelegt");
      } else {
        await api.put(`/admin/articles/${id}`, payload);
        toast.success("Artikel aktualisiert");
      }
      navigate("/admin");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const generateImage = async () => {
    if (!genPrompt.trim()) return;
    setGenerating(true);
    try {
      const res = await api.post("/admin/generate-image", { prompt: genPrompt });
      set("cover_image", res.data.data_url);
      toast.success("Bild generiert");
    } catch { toast.error("Bildgenerierung fehlgeschlagen"); }
    finally { setGenerating(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Wird geladen…</div>;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="admin-article-edit">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-brand-primary">
            <ArrowLeft size={14} /> Zurück
          </Link>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => handleSubmit("draft")} disabled={saving} data-testid="save-draft-btn">
              Als Entwurf speichern
            </Button>
            <Button className="bg-brand-primary hover:bg-brand-hover text-white rounded-none" onClick={() => handleSubmit("published")} disabled={saving} data-testid="publish-btn">
              {saving ? "Speichern…" : "Veröffentlichen"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <div>
          <Label htmlFor="title" className="text-xs uppercase tracking-wider text-neutral-500">Titel</Label>
          <Input id="title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-2 rounded-none font-serif text-2xl h-14" placeholder="Artikeltitel eingeben…" data-testid="input-title" />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Slug (URL)</Label>
            <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="automatisch aus Titel" className="mt-2 rounded-none font-mono" data-testid="input-slug" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Kategorie</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="mt-2 rounded-none" data-testid="select-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Grundlagen">Grundlagen</SelectItem>
                <SelectItem value="Vergleich">Vergleich</SelectItem>
                <SelectItem value="Praxis">Praxis</SelectItem>
                <SelectItem value="Regulierung">Regulierung</SelectItem>
                <SelectItem value="Analyse">Analyse</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-neutral-500">Teaser</Label>
          <Textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={3} className="mt-2 rounded-none" data-testid="input-excerpt" />
        </div>

        <div className="bg-white border border-neutral-200 p-6 space-y-4">
          <Label className="text-xs uppercase tracking-wider text-neutral-500">Beitragsbild</Label>
          <div className="flex gap-4 items-start">
            {form.cover_image && (
              <img
                src={form.cover_image?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.cover_image}` : form.cover_image}
                alt={form.cover_alt || "Cover"}
                className="w-40 h-28 object-cover border border-neutral-200"
              />
            )}
            <div className="flex-1 space-y-3">
              <Input value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="Bild-URL, /api/images/… oder Data-URL" className="rounded-none" data-testid="input-cover" />
              <Input value={form.cover_alt} onChange={(e) => set("cover_alt", e.target.value)} placeholder="Alt-Text für SEO (Bildbeschreibung)" className="rounded-none" data-testid="input-cover-alt" />
              <div className="flex gap-2">
                <Input value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} placeholder="KI-Bild-Prompt (z. B. 'editorial photo of wind turbines at dawn')" className="rounded-none" data-testid="input-genprompt" />
                <Button type="button" onClick={generateImage} disabled={generating} className="rounded-none whitespace-nowrap" variant="outline" data-testid="generate-image-btn">
                  {generating ? <Loader2 className="animate-spin" size={14} /> : <><Sparkles size={14} className="mr-2" /> Generieren</>}
                </Button>
              </div>
              <p className="text-xs text-neutral-500">Über Gemini Nano Banana. Ergebnisse werden als Data-URL gespeichert.</p>
            </div>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-neutral-500">Inhalt</Label>
          <div className="mt-2"><RichTextEditor value={form.content} onChange={(v) => set("content", v)} /></div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Meta Title (SEO)</Label>
            <Input value={form.meta_title} onChange={(e) => set("meta_title", e.target.value)} className="mt-2 rounded-none" data-testid="input-meta-title" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Meta Description (SEO)</Label>
            <Input value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} className="mt-2 rounded-none" data-testid="input-meta-desc" />
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-neutral-500">Lesezeit (Min.)</Label>
          <Input type="number" value={form.reading_time} onChange={(e) => set("reading_time", parseInt(e.target.value) || 1)} className="mt-2 rounded-none w-32" data-testid="input-reading-time" />
        </div>
      </main>
    </div>
  );
}
